import { getApiClient } from '../../api';
import { PrimeIcons } from 'primereact/api';
import React, { useEffect, useRef, useState } from 'react';
import useSignOut from 'react-auth-kit/hooks/useSignOut';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { Menu, Menubar, Sidebar } from '../../components/PrimereactComponents';
import { UserRole } from '../constants/enums';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { hasRole } from '../utils/auth';
import { BaseButton } from './Buttons';
import { LineDivider } from './LineDivider';
import { MunicipalityLogo } from './MunicipalityLogo';
import './AdminNavMenu.css';

export const AdminNavMenu = () => {
    const { municipalityId, bodyType, termId, meetingId, languageCode } = useParams();

    const navigate = useNavigate();

    const signOut = useSignOut();

    const [municipality, setMunicipality] = useState(null);
    const [isMenuSiderbarVisible, setIsMenuSidebarVisible] = useState(false);

    const { user } = useUserPermissions();

    const userMenuRef = useRef(null);

    useEffect(() => {
        const fetchMunicipality = async () => {
            try {
                const municipalityResponse = await getApiClient().getMunicipalityByMunicipalityId(municipalityId);
                setMunicipality(municipalityResponse);
            } catch (error) {
                navigate('/chyba');
            }
        };
        if (municipalityId) fetchMunicipality();
        else setMunicipality(null);
    }, [municipalityId, navigate]);

    const showMenuSidebar = () => {
        setIsMenuSidebarVisible(true);
    };

    const hideMenuSidebar = () => {
        setIsMenuSidebarVisible(false);
    };

    const template = (item, options) => {
        return (
            <NavLink
                to={item.url}
                className={({ isActive }) =>
                    isActive ? `menu__link-active menu-item ${options.className}` : `${options.className} menu-item`
                }
                onClick={hideMenuSidebar}
                end
            >
                <span className={`${item.icon} menu-item__icon`} />
                <span>{item.label}</span>
            </NavLink>
        );
    };

    const navItems = [];

    if (hasRole(user, UserRole.ADMIN)) {
        
        const navItemsDatabases =
        {
            label: 'Databáze',
            icon: PrimeIcons.DATABASE,
            url: '/admin/databaze',
            template: template
        };

        navItems.push(navItemsDatabases);
    }

    const navItemsMunicipalities =
    {
        label: 'Municipality',
        icon: PrimeIcons.BUILDING,
        url: '/admin/municipality/',
        template: template
    };
    
    navItems.push(navItemsMunicipalities);

    if (hasRole(user, UserRole.ADMIN)) {

        const navItemsAdmin = [
            {
                label: 'Uživatelé',
                icon: 'bi bi-person',
                url: '/admin/uzivatele',
                template: template
            },
            {
                label: 'Jazyk',
                icon: 'bi bi-globe',
                url: '/admin/jazyk',
                template: template
            },
            {
                label: 'Požadavky',
                icon: 'bi bi-patch-question',
                url: '/admin/pozadavky',
                template: template
            }
        ];
        navItemsAdmin.forEach((item) => navItems.push(item));
    }

    if (languageCode) {
        const navItemsLanguage = [
            {
                template: () => <LineDivider layout='vertical' />
            },
            {
                label: 'Číselníky',
                icon: 'bi bi-list-ul',
                url: `/admin/jazyk/${languageCode}/enums`,
                template
            },
            {
                label: 'Municipality',
                icon: 'bi bi-building',
                url: `/admin/jazyk/${languageCode}/municipality`,
                template
            },
            {
                label: 'Orgány',
                icon: 'bi bi-diagram-3',
                url: `/admin/jazyk/${languageCode}/body`,
                template
            }
        ];
        navItemsLanguage.forEach((item) => navItems.push(item));
    }

    if (municipalityId && bodyType) {
        const navItemsMunicipality = [
            {
                template: () => <LineDivider layout='vertical' />
            },
            {
                label: 'Subjekty',
                icon: 'bi bi-person-workspace',
                url: `/admin/municipality/${municipalityId}/${bodyType}/subjekty`,
                template: template
            },
            {
                label: 'Členové',
                icon: 'bi bi-people-fill',
                url: `/admin/municipality/${municipalityId}/${bodyType}/clenove`,
                template: template
            },
            {
                label: 'Funkční období',
                icon: 'bi bi-calendar-range',
                url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi`,
                template: template
            }
        ];
        navItemsMunicipality.forEach((item) => navItems.push(item));
    }

    if (termId) {
        const navItemsTerm = [
            {
                label: 'Zasedání',
                icon: 'bi bi-calendar-event',
                url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${termId}/zasedani`,
                template: template
            }
        ];
        navItemsTerm.forEach((item) => navItems.push(item));
    }

    if (meetingId) {
        const navItemsMeeting = [
            {
                label: 'Hlasování',
                icon: 'bi bi-check2-square',
                url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${termId}/zasedani/${meetingId}/hlasovani`,
                template: template
            }
        ];
        navItemsMeeting.forEach((item) => navItems.push(item));
    }

    const userTemplate = (item, options) => {
        return (
            <NavLink
                to={item.url}
                className={({ isActive }) => (isActive ? `menu__link-active ${options.className}` : options.className)}
                onClick={options.onClick}
                end
            >
                <span className={`${options.iconClassName} ${item.icon}`} />
                <span className={options.labelClassName}>{item.label}</span>
            </NavLink>
        );
    };

    const userMenuItems = [
        {
            label: `Přihlášen [${user?.login}]`,
            items: [
                {
                    label: 'Profil',
                    icon: PrimeIcons.USER,
                    url: '/admin/profil',
                    template: userTemplate
                }
            ]
        },
        { separator: true },
        {
            label: 'Odhlásit',
            icon: PrimeIcons.SIGN_OUT,
            command: () => {
                signOut();
                navigate('/admin/login');
            }
        }
    ];

    const userMenu = (
        <>
            <BaseButton
                icon={PrimeIcons.USER}
                rounded
                outlined
                onClick={(event) => userMenuRef.current.toggle(event)}
            />
            <Menu model={userMenuItems} popup ref={userMenuRef} baseZIndex={2001} />
        </>
    );

    const municipalityLogo = municipality ? <MunicipalityLogo municipality={municipality} /> : null;

    const start = (
        <>
            {municipalityLogo}
            <button
                onClick={showMenuSidebar}
                type='button'
                className='p-sidebar-icon p-link hamburger-icon menu-item'
                aria-label='Open'
            >
                <i className={PrimeIcons.BARS} />
            </button>
        </>
    );

    return (
        <div className='mainmenu mainmenu--admin'>
            <Menubar model={navItems} start={start} end={userMenu} />

            <Sidebar visible={isMenuSiderbarVisible} position='left' onHide={hideMenuSidebar}>
                {municipalityLogo}
                <Menu
                    className='sidebar-menu'
                    model={navItems.map((item) => (item.url ? item : { separator: true }))}
                />
            </Sidebar>
        </div>
    );
};
